import React, { Children, MouseEventHandler, ReactElement, ReactNode, isValidElement } from 'react';

import { withGoogleAnalyticsClickEvent, GoogleAnalyticsConstants } from '@vvoyages/google-analytics';
import classNames from 'classnames';

import Icon from '@/components/Icon';
import Svg from '@/components/Svg';

import styles from './Button.module.scss';

type ButtonVariant =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'icon'
  | 'cta'
  | 'payment-card'
  | 'btn-disabled'
  | 'red'
  | 'link';

type ButtonProps = {
  id?: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  children: ReactNode;
  variant?: ButtonVariant;
  classWrapper?: string;
  className?: string;
  disabled?: boolean;
  type?: 'button' | 'reset' | 'submit';
  tabIndex?: number;
  ariaLabel?: string;
};

const Button = ({
  id,
  onClick,
  children,
  variant = 'default',
  classWrapper = '',
  className,
  disabled = false,
  type = 'button',
  tabIndex = 0,
  ariaLabel,
}: ButtonProps) => (
  <button
    id={id || 'button'}
    // eslint-disable-next-line react/button-has-type
    type={type || 'button'}
    className={classNames(styles.default, className, {
      [styles[`btn-${variant}`]]: variant !== 'default',
      [styles[classWrapper]]: !!classWrapper,
      [styles.disabled]: !!disabled,
    })}
    onClick={onClick}
    disabled={disabled}
    tabIndex={tabIndex}
    aria-label={ariaLabel}
  >
    {children}
  </button>
);

export default withGoogleAnalyticsClickEvent(
  Button,
  (props: ButtonProps) => {
    // Guessing eventAction/eventLabel based on props
    // Any icon
    const c = Children.toArray(props.children);
    if (c.length === 1) {
      if (isValidElement(c[0]) && c[0].type === Svg) {
        return GoogleAnalyticsConstants.EVENT_ACTION.ICON_CLICK;
      }
    }

    // Special case for BackButton with BackIcon
    if (props.ariaLabel === 'Back') {
      return GoogleAnalyticsConstants.EVENT_ACTION.LINK_CLICK;
    }

    // icon with h1
    if (c.length === 2) {
      if (
        isValidElement(c[0]) &&
        c[0].type === Icon &&
        isValidElement(c[1]) &&
        c[1].type === 'h1' &&
        typeof c[1].props.children === 'string'
      ) {
        return GoogleAnalyticsConstants.EVENT_ACTION.BUTTON_CLICK;
      }

      // Text with arrow
      if (
        isValidElement(c[0]) &&
        c[0].type === 'span' &&
        isValidElement(c[1]) &&
        c[1].type === Svg &&
        typeof (c[0] as ReactElement).props.children === 'string'
      ) {
        return GoogleAnalyticsConstants.EVENT_ACTION.LINK_CLICK;
      }

      // Another text with arrow
      if (
        isValidElement(c[0]) &&
        c[0].type === 'span' &&
        isValidElement(c[1]) &&
        c[1].type === 'span' &&
        typeof c[1].props.children === 'string'
      ) {
        return GoogleAnalyticsConstants.EVENT_ACTION.LINK_CLICK;
      }
    }

    switch (props.variant) {
      case 'cta':
        // eslint-disable-next-line consistent-return
        return;
      case 'icon':
        return GoogleAnalyticsConstants.EVENT_ACTION.ICON_CLICK;
      case 'primary':
      case 'secondary':
      case 'payment-card':
      case 'default':
        return GoogleAnalyticsConstants.EVENT_ACTION.BUTTON_CLICK;
      default:
        return GoogleAnalyticsConstants.EVENT_ACTION.LINK_CLICK;
    }
  },
  (props: ButtonProps) => {
    const c = Children.toArray(props.children);

    // Special case for BackButton to pass the event label
    if (props.ariaLabel === 'Back') {
      return 'back-arrow';
    }

    // Any icon
    if (c.length === 1) {
      if (isValidElement(c[0]) && c[0].type === Svg) {
        return c[0].props.iconId;
      }
    }

    // Text with arrow
    if (c.length === 2) {
      if (
        isValidElement(c[0]) &&
        c[0].type === 'span' &&
        isValidElement(c[1]) &&
        c[1].type === Svg &&
        typeof c[0].props.children === 'string'
      ) {
        return c[0].props.children;
      }

      // icon with h1
      if (
        isValidElement(c[0]) &&
        c[0].type === Icon &&
        isValidElement(c[1]) &&
        c[1].type === 'h1' &&
        typeof c[1].props.children === 'string'
      ) {
        return c[1].props.children;
      }

      // Another text with arrow

      if (
        isValidElement(c[0]) &&
        c[0].type === 'span' &&
        isValidElement(c[1]) &&
        c[1].type === 'span' &&
        typeof (c[0] as ReactElement).props.children === 'string'
      ) {
        return (c[0] as ReactElement).props.children;
      }
    }

    switch (props.variant) {
      case 'cta':
      case 'icon':
        // eslint-disable-next-line consistent-return
        return;
      case 'primary':
      case 'secondary':
      case 'default':
      default:
        return typeof props.children === 'string' ? props.children : null;
    }
  },
);
